import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { X, Zap, Loader2, Volume2, Check, Clock } from "lucide-react";
import client from "@/lib/api";
import { useTts } from "@/hooks/useTts";
import { useLang } from "@/context/LangContext";

export default function Challenge() {
  const nav = useNavigate();
  const { play } = useTts();
  const { t } = useLang();
  const [item, setItem] = useState(null);
  const [err, setErr] = useState(false);
  const [sel, setSel] = useState(null);
  const [checked, setChecked] = useState(false);
  const [left, setLeft] = useState(20);
  const timerRef = useRef(null);

  useEffect(() => {
    client.get("/learn/challenge").then(({ data }) => { setItem(data); setLeft(data.seconds || 20); if (data.audio_text) play(data.audio_text); })
      .catch(() => setErr(true));
  }, []); // eslint-disable-line

  useEffect(() => {
    if (!item || checked) return;
    timerRef.current = setInterval(() => setLeft((l) => { if (l <= 1) { clearInterval(timerRef.current); setChecked(true); return 0; } return l - 1; }), 1000);
    return () => clearInterval(timerRef.current);
  }, [item, checked]);

  if (err) return (
    <div className="min-h-screen bg-[#F8FAFC] flex flex-col items-center justify-center px-6 text-center">
      <Zap className="text-slate-300" size={48} />
      <p className="font-bold text-[#0F172A] mt-4">{t("challenge_locked")}</p>
      <button onClick={() => nav("/home")} className="mt-6 bg-[#2563EB] text-white font-bold px-6 py-3 rounded-2xl">{t("common_back")}</button>
    </div>
  );
  if (!item) return <div className="min-h-screen bg-[#F8FAFC] flex items-center justify-center"><Loader2 className="animate-spin text-[#2563EB]" /></div>;

  const ok = sel === item.answer;
  const submit = () => { clearInterval(timerRef.current); setChecked(true); };

  return (
    <div className="min-h-screen bg-[#F8FAFC] flex flex-col px-5">
      <div className="flex items-center justify-between pt-6">
        <button onClick={() => nav("/home")} data-testid="challenge-close" className="w-10 h-10 rounded-full bg-white border border-slate-200 flex items-center justify-center"><X className="text-slate-600" size={20} /></button>
        <span className="flex items-center gap-2 font-bold text-white bg-[#2563EB] rounded-full px-3.5 py-1.5 text-sm"><Zap size={15} /> {t("challenge_title")}</span>
        <span className={`flex items-center gap-1 font-extrabold rounded-full px-3 py-1.5 ${left <= 5 ? "bg-amber-100 text-amber-700" : "bg-white border border-slate-200 text-[#0F172A]"}`} data-testid="challenge-timer"><Clock size={15} /> {left}s</span>
      </div>

      <div className="mt-3 h-2 rounded-full bg-[#EDF2F7] overflow-hidden">
        <div className="h-full rounded-full bg-gradient-to-r from-[#2563EB] to-[#3B82F6] transition-all duration-1000 ease-linear" style={{ width: `${(left / (item.seconds || 20)) * 100}%` }} />
      </div>

      <div className="flex-1 flex flex-col justify-center">
        <div className="rounded-[26px] bg-white border border-slate-200/70 shadow-[0_24px_50px_-28px_rgba(15,23,42,0.45)] overflow-hidden">
          {item.image && <img src={item.image} alt="" className="w-full h-52 object-cover object-center" data-testid="challenge-image" />}
          <div className="p-5">
            <div className="flex items-center gap-2">
              <h2 className="font-heading text-xl font-extrabold text-[#0F172A]">{item.prompt}</h2>
              {item.audio_text && <button onClick={() => play(item.audio_text)} className="w-9 h-9 rounded-full bg-[#2563EB]/10 text-[#2563EB] flex items-center justify-center shrink-0"><Volume2 size={17} /></button>}
            </div>
            <div className="mt-4 space-y-3">
              {item.options.map((o, i) => {
                let cls = "border-slate-200 bg-white text-[#0F172A]";
                if (checked && i === item.answer) cls = "border-emerald-400 bg-emerald-50 text-emerald-800";
                else if (checked && i === sel) cls = "border-rose-400 bg-rose-50 text-rose-700";
                else if (sel === i) cls = "border-[#2563EB] bg-[#2563EB]/5 text-[#0F172A]";
                return <button key={i} disabled={checked} onClick={() => setSel(i)} data-testid={`challenge-opt-${i}`} className={`w-full text-left rounded-2xl px-4 py-3.5 border font-semibold ${cls}`}>{o}</button>;
              })}
            </div>
          </div>
        </div>
      </div>

      <div className="pb-8">
        {!checked ? (
          <button onClick={submit} disabled={sel === null} data-testid="challenge-submit" className="w-full bg-[#2563EB] text-white font-bold py-4 rounded-2xl disabled:opacity-50 shadow-[0_16px_30px_-14px_rgba(37,99,235,0.85)]">{t("common_submit")}</button>
        ) : (
          <div className="text-center">
            <p className="font-heading text-xl font-extrabold text-[#0F172A] flex items-center justify-center gap-2">{ok ? <><Check className="text-[#22C55E]" /> {t("challenge_nice")}</> : t("challenge_timesup")}</p>
            <button onClick={() => nav("/home")} data-testid="challenge-done" className="w-full mt-4 bg-[#2563EB] text-white font-bold py-4 rounded-2xl">{t("common_done")}</button>
          </div>
        )}
      </div>
    </div>
  );
}
